import { createSelector } from '@reduxjs/toolkit';

import {
  FavoriteMatchesMenuState,
  MenuLayerLevel,
  RootMenuItem,
  StagesMenuLayer,
  StagesMenuLayerItem,
  TournamentsMenuLayer,
  TournamentsMenuLayerItem,
  // PROMOTION_LEVEL,
  // UnionsMenuPromotedMenuItem,
  UnionsMenuLayer,
  UnionsMenuLayerItem,
} from '../types';
import { ConstantMenuItem } from 'app/modules/Navigation/types';
import { ReduxState } from 'app/redux/types';

import { NAMESPACE_PREMATCH } from 'app/constants/Context';
import {
  favoriteMatchIds,
  favoriteTournamentIds,
} from 'app/lib/favorites/selectors';
import {
  createActiveItemDetectionSelector,
  currentLocation,
} from 'app/modules/Navigation/selectors';
import {
  composeEntityPath,
  composeSportPath,
  composeTournamentPath,
  composeUnionPath,
  createMatchMenuItem,
  createSportMenuItem,
  createStageMenuItem,
  createTournamentMenuItem,
  createUnionMenuItem,
  // createConstantMenuItem,
  getPrefixByNamespace,
} from 'app/modules/Navigation/utils';
import {
  matches as gridMatches,
  objects as gridObjects,
} from 'app/modules/Prematch/selectors/gridEntities';
import {
  entityLoading as loading,
  entityRequested as requested,
  sports,
  tournaments,
  tournamentStages,
  tournamentUnions,
} from 'app/modules/Prematch/selectors/menuEntities';
import {
  entityLoading as matchesLoading,
  matches,
  objects,
} from 'app/modules/PrematchFavorites/selectors/navigationEntities';

import {
  createStagesMenuLayer,
  createTournamentsMenuLayer,
  createUnionsMenuLayer,
} from '../utils';
import {
  applyNavigationState,
  createEntityLayersSelector,
  createEntityMenuItemsSelector,
  // createEntityMenuItemsByPromotionLevelSelector,
  groupBy,
} from './helpers';
// import dictionary from './Prematch.dictionary.json';

// --

const prefix = getPrefixByNamespace(NAMESPACE_PREMATCH);

export {
  requested,
  loading,
};

// -- empty menu layers --

const emptyUnionsMenuLayers = createEntityLayersSelector(
  sports,
  (sport) => createUnionsMenuLayer({
    sportId: sport.id,
    header: {
      title: sport.name,
      location: composeSportPath({ sportId: sport.id }, prefix),
    },
  }),
);

const emptyTournamentsMenuLayers = createEntityLayersSelector(
  tournamentUnions,
  (union) => createTournamentsMenuLayer({
    unionId: union.id,
    header: {
      title: union.name,
      location: composeUnionPath({
        sportId: union.sportId,
        tournamentUnionId: union.id,
      }, prefix),
    },
  }),
);

const emptyStagesMenuLayers = createEntityLayersSelector(
  tournaments,
  (tournament) => createStagesMenuLayer({
    tournamentId: tournament.id,
    header: {
      title: tournament.name,
      location: composeTournamentPath({
        sportId: tournament.sportId,
        tournamentUnionId: tournament.tournamentUnionId,
        tournamentId: tournament.id,
      }, prefix),
    },
  }),
);


// -- entity menu items --

const sportMenuItems = createEntityMenuItemsSelector(
  sports,
  (sport) => {
    const dataGaValues = {
      ['data-ga-event']: 'leftmenu-sportCategorySelect',
      ['data-ga-sport-category']: sport.id,
    };

    return createSportMenuItem({ sport, prefix, dataGaValues });
  },
);

const unionMenuItems = createEntityMenuItemsSelector(
  tournamentUnions,
  (union) => ({
    ...createUnionMenuItem({ union, prefix }),
    dataGaValues: {
      ['data-ga-event']: 'leftmenu-sportCountrySelect',
      ['data-ga-sport-county']: union.id,
    },
  }),
);

const tournamentMenuItems = createEntityMenuItemsSelector(
  tournaments,
  (tournament) => ({
    ...createTournamentMenuItem({ tournament, prefix }),
    dataGaValues: {
      ['data-ga-event']: 'leftmenu-sportLeagueSelect',
      ['data-ga-sport-league']: tournament.id,
    },
  }),
);

const stageMenuItems = createEntityMenuItemsSelector(
  tournamentStages,
  (stage) => ({
    ...createStageMenuItem({ stage, prefix }),
    dataGaValues: {
      ['data-ga-event']: 'leftmenu-sportChampionshipSelect',
      ['data-ga-sport-championship']: stage.id,
    },
  }),
);

const matchMenuItems = createEntityMenuItemsSelector(
  gridObjects,
  gridMatches,
  (objects, match) => ({
    ...createMatchMenuItem({ match, objects, prefix }),
    dataGaValues: {
      ['data-ga-event']: 'leftmenu-detailView',
      ['data-ga-sport-match']: match.id,
    },
  }),
);

// -- promoted entity items by promotion level --

// const promotedUnionMenuItemsByLevel =
//   createEntityMenuItemsByPromotionLevelSelector(
//     sports,
//     unionMenuItems,
//     [PROMOTION_LEVEL.SPORT],
//     () => undefined,
//   );

// const promotedTournamentMenuItemsByLevel =
//   createEntityMenuItemsByPromotionLevelSelector(
//     tournamentUnions,
//     tournamentMenuItems,
//     [PROMOTION_LEVEL.SPORT, PROMOTION_LEVEL.UNION],
//     (unions, tournamentMenuItem) => {
//       return unions.get(tournamentMenuItem.entity.tournamentUnionId)?.name;
//     },
//   );

// const promotedStageMenuItemsByLevel =
//   createEntityMenuItemsByPromotionLevelSelector(
//     tournaments,
//     stageMenuItems,
//     [PROMOTION_LEVEL.SPORT, PROMOTION_LEVEL.UNION, PROMOTION_LEVEL.TOURNAMENT],
//     (tournaments, stageMenuItem) => {
//       return tournaments.get(stageMenuItem.entity.tournamentId)?.name;
//     },
//   );


// -- all kind of promoted entity items for concrete level --

// const sportsMenuPromotedMenuItems = createSelector(
//   (state: ReduxState) => (
//     promotedUnionMenuItemsByLevel(state)?.get(PROMOTION_LEVEL.SPORT) || []
//   ),
//   (state: ReduxState) => (
//     promotedTournamentMenuItemsByLevel(state)?.get(PROMOTION_LEVEL.SPORT) || []
//   ),
//   (state: ReduxState) => (
//     promotedStageMenuItemsByLevel(state)?.get(PROMOTION_LEVEL.SPORT) || []
//   ),
//   (unionItems, tournamentItems, stageItems) => [...unionItems, ...tournamentItems, ...stageItems]
//     .map((items) => ({
//       ...items, dataGaValues: {
//         ['data-ga-event']: 'leftmenu-line-highlight',
//       },
//     })),
// );

// const unionsMenuPromotedMenuItemsBySportId = createSelector(
//   (state: ReduxState) => (
//     promotedTournamentMenuItemsByLevel(state)?.get(PROMOTION_LEVEL.UNION) || []
//   ),
//   (state: ReduxState) => (
//     promotedStageMenuItemsByLevel(state)?.get(PROMOTION_LEVEL.UNION) || []
//   ),
//   (tournamentMenuItems, stageMenuItems) => {
//     const map = new Map<number, UnionsMenuPromotedMenuItem[]>();
//
//     groupBy((item) => item.entity.sportId, tournamentMenuItems, map);
//     groupBy((item) => item.entity.sportId, stageMenuItems, map);
//
//     return map;
//   },
// );

// const tournamentsMenuPromotedMenuItemsByUnionId = createSelector(
//   (state: ReduxState) => (
//     promotedStageMenuItemsByLevel(state)?.get(PROMOTION_LEVEL.TOURNAMENT) || []
//   ),
//   (stageMenuItems) => {
//     return groupBy(
//       (item) => item.entity.tournamentUnionId,
//       stageMenuItems,
//     );
//   },
// );


// -- root menu items --

const CONSTANT_MENU_ITEMS: ConstantMenuItem[] = [
  // createConstantMenuItem({
  //   key: 'favorites',
  //   label: dictionary.favoritesLabel,
  //   title: dictionary.favoritesTitle,
  //   location: '/prematch/favorites',
  //   activeOn: 'prefix-match',
  //   dataGaValues: {
  //     ['data-ga-event']: 'leftmenu-line-favorites',
  //   },
  // }),
];

const rootMenuItemEntries = createSelector(
  // (state: ReduxState) => sportsMenuPromotedMenuItems(state),
  (state: ReduxState) => sportMenuItems(state),
  (
    // promotedItems,
    sportItems,
  ) => {
    return (
      (CONSTANT_MENU_ITEMS as RootMenuItem[])
        // .concat(promotedItems)
        .concat(sportItems)
    );
  },
);

export const rootMenuItems = createActiveItemDetectionSelector(rootMenuItemEntries);


// -- native menu items grouped by layer entity id --

const unionMenuItemsBySportId = createSelector(
  [unionMenuItems],
  (unionMenuItems) => {
    return groupBy(
      (item) => item.entity.sportId,
      unionMenuItems,
    );
  },
);

const tournamentMenuItemsByUnionId = createSelector(
  [tournamentMenuItems],
  (tournamentMenuItems) => {
    return groupBy(
      (item) => item.entity.tournamentUnionId,
      tournamentMenuItems,
    );
  },
);

const stageMenuItemsByTournamentId = createSelector(
  [stageMenuItems],
  (stageMenuItems) => {
    return groupBy(
      (item) => item.entity.tournamentId,
      stageMenuItems,
    );
  },
);

const matchMenuItemsByStageId = createSelector(
  [matchMenuItems],
  (matchMenuItems) => {
    return groupBy(
      (item) => item.entity.tournamentStageId,
      matchMenuItems
        .sort((firstItem, secondItem) => {
          const firstItemDate = firstItem.entity.startDate?.getTime();
          const secondItemDate = secondItem.entity.startDate?.getTime();
          if (firstItemDate === secondItemDate) {
            return firstItem.entity.name < secondItem.entity.name ? -1 : 1;
          }
          return firstItemDate && secondItemDate ? firstItemDate - secondItemDate : 0;
        }),
    );
  },
);

const stagesMenuLayerItemsByTournamentId = createSelector(
  stageMenuItemsByTournamentId,
  matchMenuItemsByStageId,
  (stageMenuItemsByTournamentId, matchMenuItemsByStageId) => {
    const stagesMenuLayerItems = new Map<number, StagesMenuLayerItem[]>();

    stageMenuItemsByTournamentId.forEach((stageMenuItems, tournamentId) => {
      const items: StagesMenuLayerItem[] = [];

      for (const stageMenuItem of stageMenuItems) {
        items.push(
          stageMenuItem,
          ...(matchMenuItemsByStageId.get(stageMenuItem.stageId) || []),
        );
      }

      stagesMenuLayerItems.set(tournamentId, items);
    });

    return stagesMenuLayerItems;
  },
);

// -- filled menu layers (with items) --

const filledUnionsMenuLayers = createSelector(
  emptyUnionsMenuLayers,
  // unionsMenuPromotedMenuItemsBySportId,
  unionMenuItemsBySportId,
  (
    layers,
    // promotedMenuItemsBySportId,
    unionMenuItemsBySportId,
  ) => {
    return layers.map((layer) => ({
      ...layer,
      items: (
        ([] as UnionsMenuLayerItem[])
          // .concat(promotedMenuItemsBySportId.get(layer.sportId) || [])
          .concat(unionMenuItemsBySportId.get(layer.sportId) || [])
      ),
    }));
  },
);

const filledTournamentsMenuLayers = createSelector(
  emptyTournamentsMenuLayers,
  // tournamentsMenuPromotedMenuItemsByUnionId,
  tournamentMenuItemsByUnionId,
  (
    layers,
    // promotedMenuItemsByUnionId,
    tournamentMenuItemsByUnionId,
  ) => {
    return layers.map((layer) => ({
      ...layer,
      items: (
        ([] as TournamentsMenuLayerItem[])
          // .concat(promotedMenuItemsByUnionId.get(layer.unionId) || [])
          .concat(tournamentMenuItemsByUnionId.get(layer.unionId) || [])
      ),
    }));
  },
);

const filledStagesMenuLayers = createSelector(
  emptyStagesMenuLayers,
  stagesMenuLayerItemsByTournamentId,
  (layers, stagesMenuLayerItemsByTournamentId) => {
    return layers.map((layer) => ({
      ...layer,
      items: stagesMenuLayerItemsByTournamentId.get(layer.tournamentId) || [],
    }));
  },
);

// --

export const isFavoritesLayerVisible = (state: ReduxState): boolean => {
  return (currentLocation(state).list === 'favorites');
};


// -- complete menu layers with navigation state --

export const unionsMenuLayers = createSelector(
  filledUnionsMenuLayers,
  isFavoritesLayerVisible,
  (state: ReduxState) => currentLocation(state).sportId,
  (state: ReduxState) => currentLocation(state).tournamentUnionId,
  (layers, favoritesLayerVisible, sportId, unionId): UnionsMenuLayer[] => {
    if (favoritesLayerVisible) {
      return layers;
    }

    const visibleLayerKey = sportId ? `sport-${sportId}` : null;
    const activeItemKey = unionId ? `union-${unionId}` : null;
    return applyNavigationState(layers, visibleLayerKey, activeItemKey);
  },
);

export const tournamentsMenuLayers = createSelector(
  filledTournamentsMenuLayers,
  isFavoritesLayerVisible,
  (state: ReduxState) => currentLocation(state).tournamentUnionId,
  (state: ReduxState) => currentLocation(state).tournamentId,
  (layers, favoritesLayerVisible, unionId, tournamentId): TournamentsMenuLayer[] => {
    if (favoritesLayerVisible) {
      return layers;
    }

    const visibleLayerKey = unionId ? `union-${unionId}` : null;
    const activeItemKey = tournamentId ? `tournament-${tournamentId}` : null;
    return applyNavigationState(layers, visibleLayerKey, activeItemKey);
  },
);

export const stagesAndMatchesMenuLayers = createSelector(
  filledStagesMenuLayers,
  isFavoritesLayerVisible,
  (state: ReduxState) => currentLocation(state).tournamentId,
  (state: ReduxState) => currentLocation(state).tournamentStageId,
  (state: ReduxState) => currentLocation(state).matchId,
  (layers, favoritesLayerVisible, tournamentId, stageId, matchId): StagesMenuLayer[] => {
    if (favoritesLayerVisible) {
      return layers;
    }

    const visibleLayerKey = tournamentId ? `tournament-${tournamentId}` : null;
    let activeItemKey = null;
    if (matchId) {
      activeItemKey = `match-${matchId}`;
    }
    else if (stageId) {
      activeItemKey = `stage-${stageId}`;
    }
    return applyNavigationState(layers, visibleLayerKey, activeItemKey);
  },
);

// -- favorites menu layer --

const favoriteTournaments = createSelector(
  tournaments,
  favoriteTournamentIds,
  (tournaments, favoriteIds) => {
    return tournaments.filter((tournament) => favoriteIds.has(tournament.id));
  },
);

const favoriteTournamentMenuItemEntries = createEntityMenuItemsSelector(
  tournamentUnions,
  favoriteTournaments,
  (unions, tournament) => createTournamentMenuItem({
    tournament,
    caption: unions.get(tournament.tournamentUnionId)?.name,
    location: composeTournamentPath({
      sportId: tournament.sportId,
      tournamentUnionId: tournament.tournamentUnionId,
      tournamentId: tournament.id,
    }, `${prefix}/favorites`),
  }),
);

export const favoriteTournamentMenuItems = createActiveItemDetectionSelector(
  favoriteTournamentMenuItemEntries,
);

// --

const favoriteMatches = createSelector(
  matches,
  favoriteMatchIds,
  (matches, favoriteIds) => {
    return matches.filter((match) => favoriteIds.has(match.id));
  },
);

const favoriteMatchMenuItems = createEntityMenuItemsSelector(
  objects,
  favoriteMatches,
  (objects, match) => createMatchMenuItem({
    match,
    objects,
    location: composeEntityPath({ matchId: match.id }, `${prefix}/favorites`),
  }),
);

export const favoriteMatchesMenu = createSelector(
  matchesLoading,
  createActiveItemDetectionSelector(favoriteMatchMenuItems),
  (loading, items): FavoriteMatchesMenuState => ({ loading, items }),
);


// --

export const activeLevel = (state: ReduxState): MenuLayerLevel => {
  if (!requested(state) || loading(state)) {
    return '0';
  }

  const location = currentLocation(state);

  if (location.list === 'favorites') {
    return '1';
  }

  if (location.tournamentId && (
    tournaments(state).has(location.tournamentId)
  )) {
    return '3';
  }

  if (location.tournamentUnionId && (
    tournamentUnions(state).has(location.tournamentUnionId)
  )) {
    return '2';
  }

  if (location.sportId && sports(state).has(location.sportId)) {
    return '1';
  }

  return '0';
};
